/* ═══════════ Spring, without the library ═══════════
   The app animates every sheet with Framer's

     { type: 'spring', damping: 28, stiffness: 300, mass: 0.8 }

   This prototype has no Framer, so the same physics is solved once and
   handed to the Web Animations API as a `linear()` easing with sixty
   sample points. Same curve, same overshoot, zero dependencies.

     ζ  = c / 2√(km)          damping ratio  ≈ 0.904, underdamped
     ωn = √(k/m)              natural frequency
     ωd = ωn√(1 − ζ²)         damped frequency

     x(t) = 1 − e^(−ζωn·t)·( cos(ωd·t) + (ζωn/ωd)·sin(ωd·t) ) */

export const SHEET_SPRING = { damping: 28, stiffness: 300, mass: 0.8 } as const

/** How long the app's spring takes to settle, rounded to a frame boundary. */
export const SHEET_DURATION = 450

export function springEasing(
  { damping, stiffness, mass } = SHEET_SPRING,
  duration = SHEET_DURATION,
  steps = 60,
): string {
  const zeta = damping / (2 * Math.sqrt(stiffness * mass))
  const wn = Math.sqrt(stiffness / mass)

  const points: number[] = []
  for (let i = 0; i <= steps; i += 1) {
    const t = (i / steps) * (duration / 1000)
    let v: number
    if (zeta < 1) {
      const wd = wn * Math.sqrt(1 - zeta * zeta)
      v = 1 - Math.exp(-zeta * wn * t) * (Math.cos(wd * t) + ((zeta * wn) / wd) * Math.sin(wd * t))
    } else {
      // Critically damped, the closed form the ratio collapses to.
      v = 1 - Math.exp(-wn * t) * (1 + wn * t)
    }
    points.push(v)
  }
  points[points.length - 1] = 1

  return `linear(${points.map((v) => v.toFixed(4)).join(',')})`
}
